// Assessment master data — content used VERBATIM from RoleIQ_Assessment_Descriptions_Complete.txt
// Do NOT rewrite descriptions, science sections, or interesting facts.

export const ASSESSMENT_COSTS = {
  axis: 50,
  tree: 30,
  hbdi: 80,
  hogan: 150,
  mbti: 60,
  true_colors: 30,
};

export const ASSESSMENTS = [
  {
    key: "axis",
    title: "AXIS Assessment",
    subtitle: "Understand Your Core Behavioural Drives",
    tagline: "Understand What Drives You. Lead With Intention.",
    cost: 50,
    cta: "Take AXIS Assessment",
    ctaSecondary: "View Sample Report",
    href: "/assessments/axis",
    accentColor: "gold",
    description: "AXIS is RoleIQ's proprietary behavioural assessment built specifically for the modern Indian workplace. It measures four core behavioural drives — Achievement, eXpression, Influence, and Stability — revealing how you naturally approach work, interact with others, respond to pressure, and lead teams. Unlike imported Western tools, AXIS is calibrated for the realities of Indian professional culture across manufacturing, technology, financial services, and beyond.",
    dimensions: [
      { letter: "A", name: "Achievement", line: "How you pursue goals and drive results" },
      { letter: "X", name: "eXpression", line: "How you communicate and share ideas" },
      { letter: "I", name: "Influence", line: "How you build relationships and lead others" },
      { letter: "S", name: "Stability", line: "How you manage change and maintain balance" },
    ],
    facts: [
      "Behavioural misfit — not skills gaps — accounts for 85% of hiring failures according to Leadership IQ research",
      "Teams with high drive diversity outperform homogeneous teams by 35% on complex problem-solving tasks (McKinsey, 2020)",
      "A Gallup study found that managers who understand individual employee motivations see 23% higher profitability in their teams",
      "Self-aware leaders are 4x more likely to be rated as highly effective by their direct reports",
      "AXIS takes 12 minutes to complete. Most professionals describe the results as the most accurate description of how they work that they have ever read",
    ],
    helps: {
      individual: "Understand your natural strengths, communication style, and growth edges with clarity",
      leader: "Build teams that complement each other's drives and resolve friction before it happens",
      organisation: "Make hiring, promotion, and development decisions backed by behavioural data",
    },
  },
  {
    key: "tree",
    title: "Tree Analysis — The Koch Tree Test",
    subtitle: "What Your Drawing Reveals About You",
    tagline: "One Drawing. Profound Insights.",
    cost: 30,
    cta: "Upload Your Tree Drawing",
    ctaSecondary: "See a Sample Analysis",
    href: "/assessments/tree",
    accentColor: "navy",
    description: "The Koch Tree Test is a projective psychological technique developed by Charles Koch in 1952 and validated in over 40 countries. By analysing how you draw a tree — its roots, trunk, branches, and overall composition — certified practitioners gain deep insight into your unconscious emotions, self-perception, resilience, and character. Simple to complete. Profound in what it reveals.",
    dimensions: [
      { letter: "R", name: "Roots", line: "Stability, groundedness, and connection to the past" },
      { letter: "T", name: "Trunk", line: "Inner strength, ego, and core identity" },
      { letter: "B", name: "Branches", line: "Social interaction and future aspirations" },
      { letter: "C", name: "Crown", line: "Intellectual capacity and ambition" },
    ],
    facts: [
      "The Koch Tree Test is used by psychologists, coaches, and HR practitioners in over 40 countries",
      "Children as young as 5 and executives in their 60s can complete the same test — the analysis adapts to developmental stage",
      "A 2015 study found tree drawing analysis correlates significantly with established measures of self-concept and emotional regulation",
      "Unlike most assessments, tree analysis captures your current psychological state — not a fixed personality type. It is a snapshot of who you are right now",
      "Many participants describe their tree analysis report as more accurate than years of therapy notes",
      "The test takes less than 10 minutes to complete but produces insights that practitioners typically spend 45 minutes discussing in a coaching session",
    ],
    helps: {
      individual: "Gain profound self-awareness about your emotional landscape and hidden strengths",
      leader: "Understand team members' inner world in a non-invasive, engaging, and memorable way",
      organisation: "Use as part of leadership development, coaching, and team-building programmes",
    },
  },
  {
    key: "hbdi",
    title: "HBDI — Herrmann Brain Dominance Instrument",
    subtitle: "Discover How Your Brain Prefers to Think",
    tagline: "The World's Leading Thinking Styles Assessment",
    cost: 80,
    estimatedFrom: "₹2,399",
    description: "The Herrmann Brain Dominance Instrument (HBDI) is the world's most scientifically validated measure of thinking preferences. Developed by Ned Herrmann at General Electric in the 1970s and used by over 2 million people globally, HBDI reveals how you naturally prefer to think — and how that preference shapes everything from how you solve problems to how you communicate, lead, and innovate.",
    dimensions: [
      { letter: "B", name: "Blue Analytical", line: "Logical, factual, critical thinking" },
      { letter: "G", name: "Green Sequential", line: "Organised, planned, structured thinking" },
      { letter: "R", name: "Red Interpersonal", line: "Emotional, sensory, kinesthetic thinking" },
      { letter: "Y", name: "Yellow Imaginative", line: "Holistic, intuitive, synthesising thinking" },
    ],
    facts: [
      "Used by organisations including NASA, IBM, McKinsey, Procter & Gamble, and General Electric",
      "The average professional has strong preference for 1-2 quadrants and avoidance of 1-2 others — creating predictable blind spots",
      "Teams with whole-brain diversity generate 41% more innovative solutions than homogeneous teams",
      "HBDI has been administered to over 2 million people across 60+ countries since its development",
      "Understanding your thinking preferences takes 20 minutes with HBDI. Understanding your team's collective thinking profile takes 2 hours and transforms how they work together",
    ],
  },
  {
    key: "hogan",
    title: "Hogan Assessments",
    subtitle: "The Science of Personality in the Workplace",
    tagline: "The Global Gold Standard for Leadership Assessment",
    cost: 150,
    estimatedFrom: "₹4,499",
    description: "Hogan Assessments are used by 75% of Fortune 500 companies and are widely regarded as the most scientifically rigorous personality assessment tools available for leadership and talent contexts. Unlike assessments that only measure how you present at your best, Hogan's three instruments together paint a complete picture of who you are — at your best, under pressure, and at your core.",
    dimensions: [
      { letter: "B", name: "HPI", line: "Bright Side — how you present at your best" },
      { letter: "D", name: "HDS", line: "Dark Side — what derails you under pressure" },
      { letter: "I", name: "MVPI", line: "Inside — your core values and motivations" },
    ],
    facts: [
      "Used by 75% of Fortune 500 companies for leadership selection and development",
      "The HDS was the first commercial assessment specifically designed to measure leadership derailment risk",
      "Research shows that high performers derail at senior levels not because of lack of competence but because of unchecked personality-based risk factors — exactly what HDS measures",
      "Hogan assessments have been translated into 56 languages and validated in over 60 countries",
      "A single leadership derailment costs organisations an average of $1.5 million when lost productivity, severance, and recruitment are factored in",
    ],
  },
  {
    key: "mbti",
    title: "MBTI — Myers-Briggs Type Indicator",
    subtitle: "Understand Your Personality Type",
    tagline: "The World's Most Recognised Personality Framework",
    cost: 60,
    estimatedFrom: "₹1,799",
    description: "The Myers-Briggs Type Indicator is the most widely used personality assessment in the world, taken by over 2 million people annually across 115 countries. Based on Carl Jung's theory of psychological types, MBTI identifies your preferences across four dimensions and produces one of 16 personality types — a framework that has helped generations of professionals understand themselves, work better with others, and find roles where they can genuinely thrive.",
    dimensions: [
      { letter: "E", name: "E vs I", line: "Extraversion vs Introversion — where you get energy" },
      { letter: "S", name: "S vs N", line: "Sensing vs Intuition — how you take in information" },
      { letter: "T", name: "T vs F", line: "Thinking vs Feeling — how you make decisions" },
      { letter: "J", name: "J vs P", line: "Judging vs Perceiving — how you relate to the world" },
    ],
    facts: [
      "More than 10,000 companies, 2,500 universities, and 200 government agencies use MBTI globally",
      "MBTI has been translated into 30 languages and validated across 115 countries",
      "Research shows that 94% of people who complete MBTI describe their results as accurate or very accurate",
      "MBTI is used by the US Air Force, the Singapore government, and thousands of coaching practitioners worldwide",
      "Understanding type differences reduced conflict in a major Indian IT company by 34% in a 6-month study",
    ],
  },
  {
    key: "true_colors",
    title: "True Colors Assessment",
    subtitle: "Your Personality in Four Colours",
    tagline: "Your Personality in Four Powerful Colours",
    cost: 30,
    estimatedFrom: "₹899",
    description: "True Colors is one of the most accessible and engaging personality frameworks in professional development. Created by Don Lowry in 1978 based on the work of David Keirsey, True Colors translates complex personality theory into four vivid colour metaphors — Orange, Gold, Green, and Blue — that are immediately understandable, memorable, and practically useful in any team or leadership context.",
    dimensions: [
      { letter: "O", name: "Orange", line: "Action, adventure, spontaneity" },
      { letter: "G", name: "Gold", line: "Order, responsibility, structure" },
      { letter: "G", name: "Green", line: "Intellect, analysis, independence" },
      { letter: "B", name: "Blue", line: "Harmony, empathy, authenticity" },
    ],
    facts: [
      "True Colors has been used in over 30 countries and with millions of professionals since 1978",
      "Studies show that most conflicts in teams can be traced to differences in Gold vs Orange or Green vs Blue orientations",
      "True Colors is one of the few personality frameworks that works as well with teenagers as it does with senior executives",
      "The framework is used extensively in the US military, education sector, and corporate training worldwide",
      "Most people can remember their True Colors results and apply them in conversation for years after completing the assessment — making it one of the most practically sticky tools in professional development",
    ],
  },
];

// Assessment Token Packs
export const ASSESSMENT_PACKS = [
  { name: "Assessment Starter", price: 899, tokens: 50, per: "₹17.98/token" },
  { name: "Assessment Growth", price: 1999, tokens: 150, per: "₹13.33/token", badge: "Save 26%" },
  { name: "Assessment Pro", price: 3999, tokens: 350, per: "₹11.43/token", badge: "Save 36%", featured: true },
  { name: "Assessment Power", price: 7999, tokens: 800, per: "₹9.99/token", badge: "Save 44%" },
];

export const ASSESSMENT_ORG_PACKS = [
  { name: "Assessment Team", users: "Up to 10 users", price: 9999, tokens: 500, desc: "₹999 per person for AXIS" },
  { name: "Assessment Business", users: "Up to 50 users", price: 24999, tokens: 1500, desc: "₹499 per person" },
  { name: "Assessment Enterprise", users: "100+ users", price: 49999, tokens: 4000, desc: "₹499 per person" },
];

// Combination bundles — credit BOTH map tokens and assessment tokens
export const COMBO_PACKS = [
  { name: "Explorer Bundle", price: 1499, tokens: 10, assessmentTokens: 50, badge: "Most Popular for Individuals", desc: "10 map tokens + 50 assessment tokens (1 AXIS)", save: "Save ₹299" },
  { name: "Team Starter Bundle", price: 7999, tokens: 50, assessmentTokens: 300, desc: "50 map tokens + 300 assessment tokens (6 AXIS)", save: "Save ₹1,998" },
  { name: "Organisation Growth Bundle", price: 19999, tokens: 150, assessmentTokens: 1000, badge: "Best Value", desc: "150 map tokens + 1,000 assessment tokens (20 AXIS)", save: "Save ₹4,997", featured: true },
  { name: "Enterprise Intelligence Bundle", price: 49999, tokens: 500, assessmentTokens: 3000, desc: "500 map tokens + 3,000 assessment tokens + dedicated AM", save: "Save ₹9,997" },
  { name: "RoleIQ Founder Package", price: 4999, tokens: 25, assessmentTokens: 150, badge: "Founder Rate — Limited to First 10", desc: "25 map tokens + 150 assessment tokens (3 AXIS) + onboarding call" },
];
